import { ApiError } from '../api/client';
import type { HandledCode } from '../api/errorCodes';

/**
 * Turn an API failure into per-field errors plus one form-level message.
 *
 * The backend is the source of truth for what is wrong; this only reshapes its
 * answer for rendering.
 */
export interface FormErrors {
  fields: Record<string, string>;
  message: string | null;
  requestId?: string;
}

const GENERIC_MESSAGE = 'Something went wrong. Please try again.';

/** Compare against a declared code, so a typo is a type error rather than a
 *  branch that silently never fires (`api/errorCodes.ts`). */
const is = (code: string, expected: HandledCode): boolean => code === expected;

export function toFormErrors(error: unknown): FormErrors {
  if (!(error instanceof ApiError)) {
    return { fields: {}, message: GENERIC_MESSAGE };
  }

  if (is(error.code, 'VALIDATION_ERROR')) {
    const fields: Record<string, string> = {};
    for (const [field, messages] of Object.entries(error.details)) {
      const text = Array.isArray(messages) ? messages.join(' ') : String(messages);
      if (field === 'non_field_errors') {
        continue;
      }
      fields[field] = text;
    }

    const nonField = error.details['non_field_errors'];
    return {
      fields,
      message: Array.isArray(nonField) ? nonField.join(' ') : null,
      requestId: error.requestId,
    };
  }

  if (is(error.code, 'RATE_LIMITED')) {
    const seconds = Number(error.details['retry_after_seconds'] ?? 0);
    const minutes = Math.ceil(seconds / 60);
    return {
      fields: {},
      message:
        seconds > 0
          ? `Too many attempts. Please try again in about ${minutes} minute${minutes === 1 ? '' : 's'}.`
          : 'Too many attempts. Please try again later.',
      requestId: error.requestId,
    };
  }

  return { fields: {}, message: error.message || GENERIC_MESSAGE, requestId: error.requestId };
}
